import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import {
  ArrowLeftOutlined,
  CarOutlined,
  DeliveredProcedureOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  SearchOutlined,
  ShoppingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import {
  getAllPincodesServiceability,
  getAllNonServiceablePincodes,
  getHubMapListing,
} from "../../services/api/pincodeServiceabilityApi";
import {
  PINCODE_MAP_MESSAGES,
  REQUEST_STATUSES,
} from "../../constants/pincodeMapConstants";

const T = {
  bg: "#fafaf8",
  white: "#ffffff",
  border: "#ecebe6",
  text: "#171717",
  muted: "#6b7280",
  soft: "#9ca3af",
  blue: "#2563eb",
  green: "#059669",
  orange: "#ea580c",
  purple: "#7c3aed",
  red: "#dc2626",
  teal: "#0d9488",
};

const INDIA_CENTER = [22.5937, 78.9629];
const DEFAULT_ZOOM = 5;
const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const CATEGORY_KEYS = ["courier", "ecomm", "mcn"];
const CARD_KEYS_WITH_HUBS = new Set(["all", "hubs"]);
const PIN_CACHE = {};
const { FULFILLED, REJECTED } = REQUEST_STATUSES;

const CATEGORY_CONFIG = {
  courier: {
    key: "courier",
    label: "Courier",
    mapLabel: "Courier",
    filterLabel: "Courier",
    legendLabel: "Courier",
    statLabel: "Courier",
    tone: "courier",
    Icon: CarOutlined,
  },
  ecomm: {
    key: "ecomm",
    label: "E-commerce",
    mapLabel: "E-com",
    filterLabel: "E-commerce",
    legendLabel: "E-commerce",
    statLabel: "E-commerce",
    tone: "ecomm",
    Icon: ShoppingOutlined,
  },
  mcn: {
    key: "mcn",
    label: "MCN / 3PL",
    mapLabel: "MCN",
    filterLabel: "Courier (MCN/3PL)",
    legendLabel: "MCN / 3PL",
    statLabel: "MCN / 3PL",
    tone: "mcn",
    Icon: DeliveredProcedureOutlined,
  },
  nonsvc: {
    key: "nonsvc",
    label: "Non Serviceable",
    mapLabel: "Non Svc",
    filterLabel: "Non Serviceable",
    legendLabel: "Non Serviceable",
    statLabel: "Non Serviceable",
    tone: "nonsvc",
    Icon: WarningOutlined,
  },
  hubs: {
    key: "hubs",
    label: "Active Hubs",
    mapLabel: "Hubs",
    filterLabel: "Active Hubs",
    legendLabel: "Hub",
    statLabel: "Active Hubs",
    tone: "hub",
    Icon: HomeOutlined,
  },
};

const toneColor = {
  courier: T.blue,
  ecomm: T.green,
  mcn: T.orange,
  multi: T.purple,
  nonsvc: T.red,
  hub: T.teal,
};

const DEFAULT_FILTERS = {
  courier: true,
  ecomm: true,
  mcn: true,
  nonsvc: false,
  hubs: true,
};

const CARD_FILTER_PRESETS = {
  all: DEFAULT_FILTERS,
  courier: {
    courier: true,
    ecomm: false,
    mcn: false,
    nonsvc: false,
    hubs: true,
  },
  ecomm: {
    courier: false,
    ecomm: true,
    mcn: false,
    nonsvc: false,
    hubs: true,
  },
  mcn: {
    courier: false,
    ecomm: false,
    mcn: true,
    nonsvc: false,
    hubs: true,
  },
  nonsvc: {
    courier: false,
    ecomm: false,
    mcn: false,
    nonsvc: true,
    hubs: false,
  },
  hubs: {
    courier: false,
    ecomm: false,
    mcn: false,
    nonsvc: false,
    hubs: true,
  },
};

const RESULT_LIMIT = 200;

const popupCardStyle = {
  background: T.white,
  borderRadius: 12,
  border: `1px solid ${T.border}`,
  padding: 14,
  minWidth: 210,
};

const popupHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
};

const popupMetaStyle = {
  fontSize: 11,
  color: T.soft,
};

const popupMonoStyle = {
  fontSize: 11,
  color: T.soft,
  fontFamily: "JetBrains Mono, monospace",
};

const mapControlsWrapStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  zIndex: 800,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const mapControlStyle = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.white,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  cursor: "pointer",
  color: T.muted,
};

const num = (value) => Number(value || 0).toLocaleString("en-IN");
const txt = (value) => (value == null ? "" : String(value).trim());
const time = (value) =>
  value?.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  }) || "";

const n = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const b = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "TRUE";

const pickBool = (item, keys) => keys.some((key) => b(item?.[key]));
const title = (value) =>
  txt(value)
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
const contains = (value, query) => txt(value).toLowerCase().includes(query);

const percentage = (value, total) =>
  `${total ? Math.round((value / total) * 100) : 0}% of total`;

const mapPincode = (item, index) => {
  const courier = pickBool(item, ["courier", "isCourier", "is_courier"]);
  const ecomm = pickBool(item, [
    "ecomm",
    "ecommerce",
    "e_commerce",
    "isEcomm",
  ]);
  const mcn = pickBool(item, ["mcn", "isMcn", "is_mcn", "threePl"]);
  const lat = n(item?.latitude ?? item?.lat);
  const lng = n(item?.longitude ?? item?.lng);

  return {
    id: `${txt(item?.pincode || item?.pinCode)}-${index}`,
    pincode: txt(item?.pincode ?? item?.pinCode ?? item?.pin),
    city: title(item?.city ?? item?.cityName ?? item?.district),
    state: title(item?.state ?? item?.stateName),
    zone: txt(item?.zone ?? item?.zoneName).toUpperCase(),
    courier,
    ecomm,
    mcn,
    lat,
    lng,
    hasCoordinates: lat !== null && lng !== null,
    isNonServiceable: !courier && !ecomm && !mcn,
  };
};

const mapHub = (item, index) => {
  const lat = n(item?.latitude ?? item?.lat);
  const lng = n(item?.longitude ?? item?.lng);

  return {
    id: String(item?.premiseId ?? item?.id ?? `hub-${index}`),
    premiseName: txt(item?.premiseName ?? item?.name),
    city: title(item?.city),
    state: title(item?.state),
    zone: txt(item?.zone).toUpperCase(),
    pincode: txt(item?.pincode),
    lat,
    lng,
    hasCoordinates: lat !== null && lng !== null,
  };
};

const mergePincodeDatasets = (serviceablePins, nonServiceablePins) => {
  const nonServiceableOnly = nonServiceablePins.filter((item) => item.isNonServiceable);
  const mergedByPincode = new Map();

  [...serviceablePins, ...nonServiceableOnly].forEach((item) => {
    if (!item.pincode) return;

    const existing = mergedByPincode.get(item.pincode);
    if (!existing) {
      mergedByPincode.set(item.pincode, item);
      return;
    }

    const courier = existing.courier || item.courier;
    const ecomm = existing.ecomm || item.ecomm;
    const mcn = existing.mcn || item.mcn;
    const lat = existing.lat ?? item.lat;
    const lng = existing.lng ?? item.lng;

    mergedByPincode.set(item.pincode, {
      ...existing,
      courier,
      ecomm,
      mcn,
      lat,
      lng,
      city: existing.city || item.city,
      state: existing.state || item.state,
      zone: existing.zone || item.zone,
      hasCoordinates: lat !== null && lng !== null,
      isNonServiceable: !courier && !ecomm && !mcn,
    });
  });

  return Array.from(mergedByPincode.values());
};

const getPincodeTone = (item) => {
  const activeLines = CATEGORY_KEYS.filter((key) => item[key]).length;
  if (item.isNonServiceable) return "nonsvc";
  if (activeLines > 1) return "multi";
  if (item.courier) return "courier";
  if (item.ecomm) return "ecomm";
  return "mcn";
};

const getResultTagTones = (item) => {
  if (item.isNonServiceable) return [];

  const tones = CATEGORY_KEYS.filter((key) => item[key]);
  return getPincodeTone(item) === "multi" ? [...tones, "multi"] : tones;
};

const matchesQuery = (values, query) =>
  !query || values.some((value) => contains(value, query));

const matchesCard = (item, activeCard) => {
  if (activeCard === "all") return true;
  if (activeCard === "nonsvc") return item.isNonServiceable;
  if (activeCard === "hubs") return false;
  return Boolean(item[activeCard]);
};

const matchesFilters = (item, filters) => {
  if (item.isNonServiceable) return filters.nonsvc;
  return CATEGORY_KEYS.some((key) => filters[key] && item[key]);
};

const shouldShowHubs = (activeCard, filters) =>
  filters.hubs && CARD_KEYS_WITH_HUBS.has(activeCard);

const buildPincodeStats = (items) =>
  items.reduce(
    (acc, item) => ({
      serviceable: acc.serviceable + (item.isNonServiceable ? 0 : 1),
      courier: acc.courier + (item.courier ? 1 : 0),
      ecomm: acc.ecomm + (item.ecomm ? 1 : 0),
      mcn: acc.mcn + (item.mcn ? 1 : 0),
      nonsvc: acc.nonsvc + (item.isNonServiceable ? 1 : 0),
    }),
    {
      serviceable: 0,
      courier: 0,
      ecomm: 0,
      mcn: 0,
      nonsvc: 0,
    }
  );

const buildStats = (pincodes, hubs) => ({
  ...buildPincodeStats(pincodes),
  hubs: hubs.length,
  zones: new Set(hubs.map((item) => item.zone).filter(Boolean)).size,
});

const buildMeta = (pincodes, mappedPincodes, hubs, mappedHubs) => ({
  pincodeTotal: pincodes.length,
  pincodeMapped: mappedPincodes.length,
  pincodeMissing: pincodes.length - mappedPincodes.length,
  hubTotal: hubs.length,
  hubMapped: mappedHubs.length,
});

const buildStatCards = (stats) => [
  {
    key: "all",
    label: "Total Serviceable",
    value: stats.serviceable,
    sub: "across all lines",
    tone: "multi",
    Icon: EnvironmentOutlined,
  },
  {
    key: "courier",
    label: CATEGORY_CONFIG.courier.statLabel,
    value: stats.courier,
    sub: percentage(stats.courier, stats.serviceable),
    tone: CATEGORY_CONFIG.courier.tone,
    Icon: CATEGORY_CONFIG.courier.Icon,
  },
  {
    key: "ecomm",
    label: CATEGORY_CONFIG.ecomm.statLabel,
    value: stats.ecomm,
    sub: percentage(stats.ecomm, stats.serviceable),
    tone: CATEGORY_CONFIG.ecomm.tone,
    Icon: CATEGORY_CONFIG.ecomm.Icon,
  },
  {
    key: "mcn",
    label: CATEGORY_CONFIG.mcn.statLabel,
    value: stats.mcn,
    sub: percentage(stats.mcn, stats.serviceable),
    tone: CATEGORY_CONFIG.mcn.tone,
    Icon: CATEGORY_CONFIG.mcn.Icon,
  },
  {
    key: "nonsvc",
    label: CATEGORY_CONFIG.nonsvc.statLabel,
    value: stats.nonsvc,
    sub: "no coverage",
    tone: CATEGORY_CONFIG.nonsvc.tone,
    Icon: CATEGORY_CONFIG.nonsvc.Icon,
  },
  {
    key: "hubs",
    label: CATEGORY_CONFIG.hubs.statLabel,
    value: stats.hubs,
    sub: `${num(stats.zones)} active zones`,
    tone: CATEGORY_CONFIG.hubs.tone,
    Icon: CATEGORY_CONFIG.hubs.Icon,
  },
];

const buildFilterSections = (stats) => [
  {
    title: "Business Lines",
    items: CATEGORY_KEYS.map((key) => ({
      key,
      label: CATEGORY_CONFIG[key].filterLabel,
      count: stats[key],
      tone: CATEGORY_CONFIG[key].tone,
      Icon: CATEGORY_CONFIG[key].Icon,
    })),
  },
  {
    title: "Non Serviceable",
    items: [
      {
        key: "nonsvc",
        label: CATEGORY_CONFIG.nonsvc.filterLabel,
        count: stats.nonsvc,
        tone: CATEGORY_CONFIG.nonsvc.tone,
        Icon: CATEGORY_CONFIG.nonsvc.Icon,
      },
    ],
  },
  {
    title: "Hubs",
    items: [
      {
        key: "hubs",
        label: CATEGORY_CONFIG.hubs.filterLabel,
        count: stats.hubs,
        tone: CATEGORY_CONFIG.hubs.tone,
        Icon: CATEGORY_CONFIG.hubs.Icon,
      },
    ],
  },
];

const legendItems = [
  CATEGORY_CONFIG.courier,
  CATEGORY_CONFIG.ecomm,
  CATEGORY_CONFIG.mcn,
  {
    key: "multi",
    legendLabel: "Multiple",
    tone: "multi",
  },
  CATEGORY_CONFIG.nonsvc,
  CATEGORY_CONFIG.hubs,
];

const buildMapInfoItems = (stats) => [
  {
    key: "courier",
    label: CATEGORY_CONFIG.courier.mapLabel,
    value: stats.courier,
    tone: CATEGORY_CONFIG.courier.tone,
    Icon: CATEGORY_CONFIG.courier.Icon,
  },
  {
    key: "ecomm",
    label: CATEGORY_CONFIG.ecomm.mapLabel,
    value: stats.ecomm,
    tone: CATEGORY_CONFIG.ecomm.tone,
    Icon: CATEGORY_CONFIG.ecomm.Icon,
  },
  {
    key: "mcn",
    label: CATEGORY_CONFIG.mcn.mapLabel,
    value: stats.mcn,
    tone: CATEGORY_CONFIG.mcn.tone,
    Icon: CATEGORY_CONFIG.mcn.Icon,
  },
  {
    key: "nonsvc",
    label: CATEGORY_CONFIG.nonsvc.mapLabel,
    value: stats.nonsvc,
    tone: CATEGORY_CONFIG.nonsvc.tone,
    Icon: CATEGORY_CONFIG.nonsvc.Icon,
  },
  {
    key: "hubs",
    label: CATEGORY_CONFIG.hubs.mapLabel,
    value: stats.hubs,
    tone: CATEGORY_CONFIG.hubs.tone,
    Icon: CATEGORY_CONFIG.hubs.Icon,
  },
];

const chipStyle = (tone, active) => ({
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 600,
  background: active ? `${toneColor[tone]}15` : "#f5f5f5",
  color: active ? toneColor[tone] : T.soft,
  border: `1px solid ${active ? `${toneColor[tone]}33` : T.border}`,
});

const pinIcon = (tone) => {
  if (!PIN_CACHE[tone]) {
    PIN_CACHE[tone] = L.divIcon({
      className: "",
      html: `<div class="pinmap-pin ${tone}"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      popupAnchor: [0, -10],
    });
  }

  return PIN_CACHE[tone];
};

const hubIcon = L.divIcon({
  className: "",
  html: `
    <div class="pinmap-hub">
      <div class="pinmap-hub-pulse"></div>
      <div class="pinmap-hub-core">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#0d9488"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35z"></path>
          <path d="M6 18h12"></path>
          <path d="M6 14h12"></path>
        </svg>
      </div>
    </div>
  `,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
  popupAnchor: [0, -18],
});

const clusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const allNonServiceable = cluster
    .getAllChildMarkers()
    .every((marker) => marker.options.markerTone === "nonsvc");
  const size = count < 10 ? 34 : count < 100 ? 42 : 50;

  return L.divIcon({
    className: "",
    html: `<div class="pinmap-cluster ${
      allNonServiceable ? "nonsvc" : ""
    }" style="width:${size}px;height:${size}px">${
      count > 999 ? "999+" : count
    }</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const StatCard = ({ item, active, onSelect }) => {
  const { key, label, value, sub, tone, Icon } = item;

  return (
    <button
      type="button"
      className={`pinmap-stat-card ${active ? "pinmap-active" : ""}`}
      onClick={() => onSelect(key)}
      style={{ "--pinmap-tone": toneColor[tone] }}
    >
      <div className="pinmap-stat-label">
        <span className="pinmap-stat-icon">
          <Icon />
        </span>
        {label}
      </div>
      <div className="pinmap-stat-value">{num(value)}</div>
      <div className="pinmap-stat-sub">{sub}</div>
      <div className="pinmap-stat-bar"></div>
    </button>
  );
};

const FilterItem = ({ item, checked, onToggle }) => {
  const { label, tone, count, Icon } = item;

  return (
    <button type="button" className="pinmap-filter-item" onClick={onToggle}>
      <span
        className={`pinmap-filter-check ${checked ? "checked" : ""}`}
        style={{ "--pinmap-tone": toneColor[tone] }}
      >
        {checked ? "✓" : ""}
      </span>
      <span className="pinmap-filter-icon">
        <Icon />
      </span>
      <span className="pinmap-filter-label">{label}</span>
      <span className="pinmap-filter-count">{num(count)}</span>
    </button>
  );
};

const Legend = () => (
  <div className="pinmap-legend-grid">
    {legendItems.map((item) => {
      const Icon = item.Icon;

      return (
        <div key={item.key} className="pinmap-legend-item">
          {Icon ? (
            <Icon style={{ color: toneColor[item.tone] }} />
          ) : (
            <span
              className="pinmap-legend-dot"
              style={{ background: toneColor[item.tone] }}
            ></span>
          )}
          {item.legendLabel}
        </div>
      );
    })}
  </div>
);

const ResultRow = ({ item, onFocus }) => (
  <button
    type="button"
    className="pinmap-result-row"
    onClick={() => onFocus(item, "pincode")}
  >
    <div className="pinmap-result-pin">{item.pincode}</div>
    <div className="pinmap-result-city">
      {[item.city, item.state].filter(Boolean).join(", ") || "Mapped pincode"}
    </div>
    {item.isNonServiceable ? (
      <span className="pinmap-result-nonsvc">NO SVC</span>
    ) : (
      <div className="pinmap-result-tags">
        {getResultTagTones(item).map((tone) => (
          <span
            key={`${item.id}-${tone}`}
            className="pinmap-result-tag"
            style={{ background: toneColor[tone] }}
          ></span>
        ))}
      </div>
    )}
  </button>
);

const PincodePopup = ({ item }) => {
  const tone = getPincodeTone(item);

  return (
    <div style={popupCardStyle}>
      <div style={popupHeaderStyle}>
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            background: toneColor[tone],
          }}
        ></div>
        <div>
          <div style={{ fontWeight: 700, color: T.text }}>{item.pincode}</div>
          <div style={popupMetaStyle}>
            {[item.city, item.state, item.zone].filter(Boolean).join(" • ") ||
              "Mapped pincode"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {CATEGORY_KEYS.map((key) => (
          <span key={key} style={chipStyle(CATEGORY_CONFIG[key].tone, item[key])}>
            {CATEGORY_CONFIG[key].label}
          </span>
        ))}
        {item.isNonServiceable && (
          <span style={chipStyle(CATEGORY_CONFIG.nonsvc.tone, true)}>
            {CATEGORY_CONFIG.nonsvc.label}
          </span>
        )}
      </div>
      <div style={popupMonoStyle}>
        {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
      </div>
    </div>
  );
};

const HubPopup = ({ item }) => (
  <div style={popupCardStyle}>
    <div style={{ ...popupHeaderStyle, marginBottom: 8 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          display: "grid",
          placeItems: "center",
          background: `${T.teal}15`,
          color: T.teal,
        }}
      >
        <HomeOutlined />
      </div>
      <div>
        <div style={{ fontWeight: 700, color: T.text }}>{item.premiseName}</div>
        <div style={popupMetaStyle}>
          {[item.city, item.state, item.zone].filter(Boolean).join(" • ") ||
            "Active hub"}
        </div>
      </div>
    </div>
    <div style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>
      Pincode {item.pincode || "NA"}
    </div>
    <div style={popupMonoStyle}>
      {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
    </div>
  </div>
);

const MapControls = ({ onZoomIn, onZoomOut, onReset }) => (
  <div style={mapControlsWrapStyle}>
    {[
      { key: "zoom-in", label: "+", onClick: onZoomIn },
      { key: "zoom-out", label: "−", onClick: onZoomOut },
      { key: "reset", label: "⌂", onClick: onReset },
    ].map((control) => (
      <button
        key={control.key}
        type="button"
        onClick={control.onClick}
        style={mapControlStyle}
      >
        {control.label}
      </button>
    ))}
  </div>
);

const MapInfo = ({ items }) => (
  <div className="pinmap-map-info">
    {items.map(({ key, label, value, tone, Icon }) => (
      <span key={key} className="pinmap-map-info-item">
        <Icon style={{ color: toneColor[tone] }} /> {label} <b>{num(value)}</b>
      </span>
    ))}
  </div>
);

const loadingStyle = {
  minHeight: "70vh",
  display: "grid",
  placeItems: "center",
  background: T.bg,
  color: T.muted,
};

const PINMAP_STYLES = `
  .pinmap-page{background:${T.bg};min-height:100%;font-family:Outfit,sans-serif;color:${T.text}}
  .pinmap-topbar{display:flex;align-items:center;justify-content:space-between;padding:14px 28px;background:${T.white};border-bottom:1px solid ${T.border};position:sticky;top:64px;z-index:950;gap:12px;flex-wrap:wrap}
  .pinmap-topbar-left{display:flex;align-items:center;gap:12px}
  .pinmap-back-arrow{font-size:18px;cursor:pointer;color:${T.muted}}
  .pinmap-back-arrow:hover{color:${T.blue}}
  .pinmap-logo-icon{width:36px;height:36px;background:${T.blue};border-radius:10px;display:grid;place-items:center;color:#fff;font-size:17px}
  .pinmap-logo-text{font-size:17px;font-weight:700;letter-spacing:-.3px}
  .pinmap-logo-text span{color:${T.muted};font-weight:400}
  .pinmap-last-sync{display:flex;align-items:center;gap:6px;font-family:JetBrains Mono, monospace;font-size:11px;color:${T.soft}}
  .pinmap-sync-dot{width:6px;height:6px;border-radius:50%;background:${T.green};animation:pinmapDot 2s infinite}
  .pinmap-sync-dot.refreshing{animation-duration:.9s}
  .pinmap-alert-bar{display:flex;align-items:center;gap:10px;padding:10px 28px;background:#fffbeb;border-bottom:1px solid #fde68a;font-size:13px;color:#92400e;flex-wrap:wrap;position:sticky;top:128px;z-index:940}
  .pinmap-alert-count{font-family:JetBrains Mono, monospace;font-weight:600;background:rgba(234,179,8,.15);padding:2px 8px;border-radius:4px}
  .pinmap-stats-row{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:12px;padding:20px 28px 0}
  .pinmap-stat-card{background:${T.white};border:1px solid ${T.border};border-radius:14px;padding:18px 20px;position:relative;overflow:hidden;cursor:pointer;transition:border-color .2s,box-shadow .2s;text-align:left}
  .pinmap-stat-card:hover{border-color:rgba(0,0,0,.1);box-shadow:0 4px 20px rgba(0,0,0,.04)}
  .pinmap-stat-card.pinmap-active{border-color:${T.blue};box-shadow:0 0 0 1px ${T.blue},0 4px 20px rgba(37,99,235,.08)}
  .pinmap-stat-label{font-size:11px;font-weight:500;color:${T.muted};text-transform:uppercase;letter-spacing:.7px;margin-bottom:8px;display:flex;align-items:center;gap:7px}
  .pinmap-stat-icon{width:30px;height:30px;border-radius:8px;display:grid;place-items:center;background:color-mix(in srgb, var(--pinmap-tone) 10%, white);color:var(--pinmap-tone)}
  .pinmap-stat-value{font-size:28px;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:-1px;line-height:1;color:var(--pinmap-tone)}
  .pinmap-stat-sub{font-size:11px;color:${T.soft};margin-top:5px;font-family:JetBrains Mono, monospace}
  .pinmap-stat-bar{position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--pinmap-tone),transparent)}
  .pinmap-main{display:grid;grid-template-columns:340px minmax(0,1fr);gap:0;height:calc(100vh - 195px);margin-top:14px;padding:0 28px 28px}
  .pinmap-sidebar{background:${T.white};border:1px solid ${T.border};border-radius:14px 0 0 14px;display:flex;flex-direction:column;overflow:hidden;min-height:0}
  .pinmap-sidebar-section{padding:14px 18px;border-bottom:1px solid ${T.border}}
  .pinmap-section-title{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1.2px;color:${T.soft};margin-bottom:10px}
  .pinmap-search-box{display:flex;align-items:center;gap:10px;background:#f5f5f2;border:1px solid ${T.border};border-radius:10px;padding:9px 12px;color:${T.soft}}
  .pinmap-search-box input{border:none;background:none;outline:none;font-family:JetBrains Mono, monospace;font-size:13px;color:${T.text};width:100%}
  .pinmap-filter-group{display:flex;flex-direction:column;gap:3px}
  .pinmap-filter-item{display:flex;align-items:center;gap:9px;padding:7px 10px;border-radius:7px;cursor:pointer;transition:background .15s;user-select:none;border:none;background:transparent;text-align:left;width:100%;color:${T.text}}
  .pinmap-filter-item:hover{background:#f5f5f2}
  .pinmap-filter-check{width:16px;height:16px;border-radius:4px;border:2px solid rgba(0,0,0,.1);display:grid;place-items:center;transition:all .15s;flex-shrink:0;font-size:10px;font-weight:700;color:#fff}
  .pinmap-filter-check.checked{border-color:transparent;background:var(--pinmap-tone)}
  .pinmap-filter-icon{display:inline-flex;align-items:center;justify-content:center;color:${T.muted}}
  .pinmap-filter-label{font-size:12px;font-weight:500;flex:1}
  .pinmap-filter-count{font-family:JetBrains Mono, monospace;font-size:11px;color:${T.soft};font-weight:500}
  .pinmap-legend-grid{display:grid;grid-template-columns:1fr 1fr;gap:5px}
  .pinmap-legend-item{display:flex;align-items:center;gap:5px;font-size:11px;color:${T.muted}}
  .pinmap-legend-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
  .pinmap-results-header{padding:12px 18px;border-bottom:1px solid ${T.border};display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;background:${T.white};z-index:2}
  .pinmap-results-title{margin:0}
  .pinmap-results-count{font-family:JetBrains Mono, monospace;font-size:11px;color:${T.muted}}
  .pinmap-results-count b{color:${T.blue}}
  .pinmap-results-section{flex:1;overflow-y:auto;min-height:0}
  .pinmap-results-section::-webkit-scrollbar{width:3px}
  .pinmap-results-section::-webkit-scrollbar-thumb{background:rgba(0,0,0,.1);border-radius:2px}
  .pinmap-result-row{display:flex;align-items:center;gap:10px;padding:8px 18px;border:none;border-bottom:1px solid rgba(0,0,0,.03);cursor:pointer;transition:background .15s;background:transparent;text-align:left;width:100%}
  .pinmap-result-row:hover{background:#f5f5f2}
  .pinmap-result-pin{font-family:JetBrains Mono, monospace;font-size:12px;font-weight:600;color:${T.text};min-width:55px}
  .pinmap-result-city{font-size:11px;color:${T.muted};flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .pinmap-result-tags{display:flex;gap:3px}
  .pinmap-result-tag{width:7px;height:7px;border-radius:50%}
  .pinmap-result-nonsvc{font-size:9px;font-weight:600;color:${T.red};background:rgba(220,38,38,.06);padding:1px 5px;border-radius:3px}
  .pinmap-empty{padding:16px;font-size:12px;color:${T.soft}}
  .pinmap-map-wrap{position:relative;background:${T.white};border-top:1px solid ${T.border};border-right:1px solid ${T.border};border-bottom:1px solid ${T.border};border-radius:0 14px 14px 0;overflow:hidden;min-height:620px}
  .pinmap-map-info{
    position:absolute;left:14px;bottom:14px;z-index:800;display:flex;flex-wrap:wrap;gap:14px;background:${T.white};
    border:1px solid ${T.border};border-radius:10px;padding:10px 14px;box-shadow:0 4px 16px rgba(0,0,0,.08);font-size:11px;color:${T.muted}
  }
  .pinmap-map-info-item{display:flex;align-items:center;gap:5px}
  .pinmap-map-info-item b{font-family:JetBrains Mono, monospace;color:${T.text}}
  .leaflet-popup-content-wrapper{background:transparent!important;box-shadow:none!important;padding:0!important}
  .leaflet-popup-content{margin:0!important}
  .leaflet-popup-tip-container,.leaflet-control-zoom{display:none!important}
  .leaflet-control-attribution{font-size:10px!important;background:rgba(255,255,255,.86)!important}
  .pinmap-pin{width:11px;height:11px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)}
  .pinmap-pin.courier{background:${T.blue}}
  .pinmap-pin.ecomm{background:${T.green}}
  .pinmap-pin.mcn{background:${T.orange}}
  .pinmap-pin.multi{background:${T.purple}}
  .pinmap-pin.nonsvc{background:${T.red}}
  .pinmap-hub{width:32px;height:32px;position:relative;display:flex;align-items:center;justify-content:center}
  .pinmap-hub-pulse{position:absolute;width:36px;height:36px;border-radius:50%;background:rgba(13,148,136,.12);animation:pinmapPulse 2.5s ease-out infinite;z-index:1}
  .pinmap-hub-core{width:28px;height:28px;background:#fff;border:2px solid ${T.teal};border-radius:7px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 10px rgba(13,148,136,.35);position:relative;z-index:2}
  .pinmap-cluster{display:flex;align-items:center;justify-content:center;border-radius:50%;color:#fff;font-weight:700;font-family:JetBrains Mono, monospace;border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.2);background:rgba(37,99,235,.9)}
  .pinmap-cluster.nonsvc{background:rgba(220,38,38,.8)}
  @keyframes pinmapDot{0%,100%{opacity:1}50%{opacity:.3}}
  @keyframes pinmapPulse{0%{transform:scale(.6);opacity:.7}100%{transform:scale(2);opacity:0}}
  @media (max-width: 1360px){
    .pinmap-stats-row{grid-template-columns:repeat(3,minmax(0,1fr))}
  }
  @media (max-width: 1100px){
    .pinmap-main{grid-template-columns:1fr;height:auto;padding:0 16px 16px}
    .pinmap-sidebar{border-radius:14px}
    .pinmap-map-wrap{border:1px solid ${T.border};border-radius:14px;margin-top:14px;min-height:520px}
  }
  @media (max-width: 600px){
    .pinmap-topbar,.pinmap-alert-bar{padding-left:16px;padding-right:16px}
    .pinmap-stats-row{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;padding:12px 16px 0}
    .pinmap-main{padding:0 16px 16px}
    .pinmap-stat-value{font-size:22px}
    .pinmap-map-info{right:14px}
  }
`;

const PincodeMap = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCard, setActiveCard] = useState("all");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pincodes, setPincodes] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [meta, setMeta] = useState({
    pincodeTotal: 0,
    pincodeMapped: 0,
    pincodeMissing: 0,
    hubTotal: 0,
    hubMapped: 0,
  });
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const mapRef = useRef(null);
  const markerRefs = useRef({ pincode: new Map(), hub: new Map() });

  const markerRef = useCallback(
    (type, id) => (marker) => {
      if (marker) markerRefs.current[type].set(id, marker);
      else markerRefs.current[type].delete(id);
    },
    []
  );

  const resetMapView = useCallback(() => {
    mapRef.current?.setView(INDIA_CENTER, DEFAULT_ZOOM, { animate: true });
  }, []);

  const focus = useCallback((item, type) => {
    if (!mapRef.current) return;

    mapRef.current.setView([item.lat, item.lng], type === "hub" ? 9 : 11, {
      animate: true,
    });
    window.setTimeout(() => markerRefs.current[type].get(item.id)?.openPopup(), 150);
  }, []);

  const loadData = useCallback(async () => {
    setRefreshing(true);

    const [serviceablePinsResult, nonServiceablePinsResult, hubsResult] = await Promise.allSettled([
      getAllPincodesServiceability(),
      getAllNonServiceablePincodes(),
      getHubMapListing(),
    ]);

    let mappedPincodes = [];
    let mappedHubs = [];
    let allPincodes = [];
    let allHubs = [];

    if (
      serviceablePinsResult.status === FULFILLED ||
      nonServiceablePinsResult.status === FULFILLED
    ) {
      const serviceablePins =
        serviceablePinsResult.status === FULFILLED
          ? (serviceablePinsResult.value?.data || []).map(mapPincode)
          : [];
      const nonServiceablePins =
        nonServiceablePinsResult.status === FULFILLED
          ? (nonServiceablePinsResult.value?.data || []).map(mapPincode)
          : [];

      allPincodes = mergePincodeDatasets(serviceablePins, nonServiceablePins);
      mappedPincodes = allPincodes.filter(
        (item) => item.hasCoordinates && item.pincode
      );
      setPincodes(mappedPincodes);

      if (serviceablePinsResult.status === REJECTED) {
        message.warning(
          PINCODE_MAP_MESSAGES.SERVICEABLE_PINS_FAILED_SHOWING_NON_SERVICEABLE
        );
      }
      if (nonServiceablePinsResult.status === REJECTED) {
        message.warning(PINCODE_MAP_MESSAGES.NON_SERVICEABLE_PINS_FAILED);
      }
    } else {
      setPincodes([]);
      message.error(PINCODE_MAP_MESSAGES.PINCODE_SERVICEABILITY_LOAD_FAILED);
    }

    if (hubsResult.status === FULFILLED) {
      allHubs = (hubsResult.value?.data || []).map(mapHub);
      mappedHubs = allHubs.filter(
        (item) => item.hasCoordinates && item.premiseName
      );
      setHubs(mappedHubs);
    } else {
      setHubs([]);
      message.error(PINCODE_MAP_MESSAGES.HUB_LIST_LOAD_FAILED);
    }

    setMeta(buildMeta(allPincodes, mappedPincodes, allHubs, mappedHubs));
    setLastRefreshed(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => buildStats(pincodes, hubs), [hubs, pincodes]);
  const statCards = useMemo(() => buildStatCards(stats), [stats]);
  const filterSections = useMemo(() => buildFilterSections(stats), [stats]);
  const query = search.trim().toLowerCase();

  const visiblePincodes = useMemo(
    () =>
      pincodes.filter(
        (item) =>
          matchesCard(item, activeCard) &&
          matchesFilters(item, filters) &&
          matchesQuery([item.pincode, item.city, item.state, item.zone], query)
      ),
    [activeCard, filters, pincodes, query]
  );

  const visibleHubs = useMemo(
    () =>
      shouldShowHubs(activeCard, filters)
        ? hubs.filter((item) => {
          const fields = [
            item.premiseName,
            item.city,
            item.state,
            item.zone,
            item.pincode,
          ];
          return matchesQuery(fields, query);
        })
        : [],
    [activeCard, filters, hubs, query]
  );

  const visibleStats = useMemo(
    () => ({
      ...buildPincodeStats(visiblePincodes),
      hubs: visibleHubs.length,
    }),
    [visibleHubs.length, visiblePincodes]
  );

  const mapInfoItems = useMemo(
    () => buildMapInfoItems(visibleStats),
    [visibleStats]
  );

  const results = useMemo(
    () => visiblePincodes.slice(0, RESULT_LIMIT),
    [visiblePincodes]
  );

  useEffect(() => {
    if (!query || !mapRef.current) return;

    const exactPin = visiblePincodes.find((item) => item.pincode === query);
    if (exactPin) focus(exactPin, "pincode");
  }, [focus, query, visiblePincodes]);

  const handleCardSelect = useCallback(
    (key) => {
      setActiveCard(key);
      setFilters(CARD_FILTER_PRESETS[key] || DEFAULT_FILTERS);

      if (key === "hubs") {
        resetMapView();
      }
    },
    [resetMapView]
  );

  const toggleFilter = useCallback((key) => {
    setActiveCard("all");
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (loading) {
    return <div style={loadingStyle}>Loading pincode coverage dashboard...</div>;
  }

  return (
    <React.Fragment>
      <div className="pinmap-page">
        <div className="pinmap-topbar">
          <div className="pinmap-topbar-left">
            <ArrowLeftOutlined
              className="pinmap-back-arrow"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            />
            <div className="pinmap-logo-icon">
              <EnvironmentOutlined />
            </div>
            <div className="pinmap-logo-text">
              Pincode Coverage <span>/ Intelligence</span>
            </div>
          </div>
          <div className="pinmap-last-sync">
            <span
              className={`pinmap-sync-dot ${refreshing ? "refreshing" : ""}`}
            ></span>
            Last synced {time(lastRefreshed)}
          </div>
        </div>

        {meta.pincodeMissing > 0 && (
          <div className="pinmap-alert-bar">
            <WarningOutlined />
            <span className="pinmap-alert-count">{num(meta.pincodeMissing)}</span>
            <span>
              pincodes missing lat/lng coordinates — not rendered on map
            </span>
          </div>
        )}

        <div className="pinmap-stats-row">
          {statCards.map((item) => (
            <StatCard
              key={item.key}
              item={item}
              active={activeCard === item.key}
              onSelect={handleCardSelect}
            />
          ))}
        </div>

        <div className="pinmap-main">
          <div className="pinmap-sidebar">
            <div className="pinmap-sidebar-section">
              <div className="pinmap-section-title">Search</div>
              <div className="pinmap-search-box">
                <SearchOutlined />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search pincode, city, hub..."
                />
              </div>
            </div>

            {filterSections.map((section) => (
              <div key={section.title} className="pinmap-sidebar-section">
                <div className="pinmap-section-title">{section.title}</div>
                <div className="pinmap-filter-group">
                  {section.items.map((item) => (
                    <FilterItem
                      key={item.key}
                      item={item}
                      checked={filters[item.key]}
                      onToggle={() => toggleFilter(item.key)}
                    />
                  ))}
                </div>
              </div>
            ))}

            <div className="pinmap-sidebar-section">
              <div className="pinmap-section-title">Legend</div>
              <Legend />
            </div>

            <div className="pinmap-results-header">
              <div className="pinmap-section-title pinmap-results-title">
                Results
              </div>
              <div className="pinmap-results-count">
                Showing <b>{num(visiblePincodes.length)}</b>
              </div>
            </div>

            <div className="pinmap-results-section">
              {results.length ? (
                results.map((item) => (
                  <ResultRow key={item.id} item={item} onFocus={focus} />
                ))
              ) : (
                <div className="pinmap-empty">
                  No records match the current filters.
                </div>
              )}
            </div>
          </div>

          <div className="pinmap-map-wrap">
            <MapContainer
              center={INDIA_CENTER}
              zoom={DEFAULT_ZOOM}
              ref={mapRef}
              style={{ height: "100%", minHeight: 620, width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution={
                  "&copy; <a href=\"https://www.openstreetmap.org/\">OpenStreetMap</a> contributors &copy; CARTO"
                }
                url={TILE_URL}
              />
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                spiderfyOnMaxZoom
                zoomToBoundsOnClick
                maxClusterRadius={40}
                disableClusteringAtZoom={10}
                iconCreateFunction={clusterIcon}
              >
                {visiblePincodes.map((item) => {
                  const tone = getPincodeTone(item);

                  return (
                    <Marker
                      key={item.id}
                      position={[item.lat, item.lng]}
                      icon={pinIcon(tone)}
                      markerTone={tone}
                      ref={markerRef("pincode", item.id)}
                    >
                      <Popup closeButton={false} maxWidth={260}>
                        <PincodePopup item={item} />
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>

              {visibleHubs.map((item) => (
                <Marker
                  key={item.id}
                  position={[item.lat, item.lng]}
                  icon={hubIcon}
                  ref={markerRef("hub", item.id)}
                >
                  <Popup closeButton={false} maxWidth={260}>
                    <HubPopup item={item} />
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            <MapControls
              onZoomIn={() => mapRef.current?.zoomIn()}
              onZoomOut={() => mapRef.current?.zoomOut()}
              onReset={resetMapView}
            />

            <MapInfo items={mapInfoItems} />
          </div>
        </div>

        <style>{PINMAP_STYLES}</style>
      </div>
    </React.Fragment>
  );
};

export default PincodeMap;
